'use client';

import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { Brain, TrendingUp, TrendingDown, Minus, Calendar, Zap, Award, Activity, Target, Shield, Clock, Sparkles, AlertCircle, CheckCircle2, Milestone } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

type TimeRange = '4w' | '3m' | '6m' | '12m';

interface WeekPoint {
  label: string;
  attention: number;
  memory: number;
  processing: number;
  executive: number;
  sessions: number;
  alphaCoherence: number;
  thetaPower: number;
  betaPower: number;
  gammaPower: number;
  neuroplasticity: number;
}

interface ClinicalMilestone {
  week: number;
  label: string;
  type: 'achievement' | 'warning' | 'info';
  detail: string;
}

// ─── Mock longitudinal data generator (fallback when no real data) ────────────

function generateLongitudinalData(weeks: number): WeekPoint[] {
  const data: WeekPoint[] = [];
  let attention = 42 + Math.random() * 8;
  let memory = 38 + Math.random() * 8;
  let processing = 45 + Math.random() * 8;
  let executive = 40 + Math.random() * 8;
  let alphaCoherence = 0.48 + Math.random() * 0.06;
  let thetaPower = 18 + Math.random() * 4;
  let betaPower = 22 + Math.random() * 4;
  let gammaPower = 8 + Math.random() * 3;
  let neuroplasticity = 34 + Math.random() * 8;

  for (let i = 0; i < weeks; i++) {
    const trend = 0.6 + Math.random() * 0.8;
    const noise = () => (Math.random() - 0.4) * 3;

    attention = Math.min(98, Math.max(20, attention + trend * 0.5 + noise()));
    memory = Math.min(98, Math.max(20, memory + trend * 0.45 + noise()));
    processing = Math.min(98, Math.max(20, processing + trend * 0.55 + noise()));
    executive = Math.min(98, Math.max(20, executive + trend * 0.4 + noise()));
    alphaCoherence = Math.min(0.95, Math.max(0.3, alphaCoherence + 0.003 + (Math.random() - 0.4) * 0.015));
    thetaPower = Math.min(35, Math.max(10, thetaPower + (Math.random() - 0.5) * 1.2));
    betaPower = Math.min(40, Math.max(12, betaPower + trend * 0.1 + (Math.random() - 0.4) * 1.5));
    gammaPower = Math.min(20, Math.max(4, gammaPower + trend * 0.08 + (Math.random() - 0.4) * 0.8));
    neuroplasticity = Math.min(98, Math.max(15, neuroplasticity + trend * 0.7 + noise()));

    const weekLabel = i < 4 ? `W${i + 1}` : `M${Math.ceil((i + 1) / 4.3)}`;

    data.push({
      label: weekLabel,
      attention: Math.round(attention),
      memory: Math.round(memory),
      processing: Math.round(processing),
      executive: Math.round(executive),
      sessions: Math.floor(2 + Math.random() * 5),
      alphaCoherence: parseFloat(alphaCoherence.toFixed(3)),
      thetaPower: parseFloat(thetaPower.toFixed(1)),
      betaPower: parseFloat(betaPower.toFixed(1)),
      gammaPower: parseFloat(gammaPower.toFixed(1)),
      neuroplasticity: Math.round(neuroplasticity),
    });
  }
  return data;
}

// ─── Real data fetcher ────────────────────────────────────────────────────────

async function fetchRealLongitudinalData(weeks: number): Promise<WeekPoint[] | null> {
  try {
    const supabase = createClient();
    const since = new Date();
    since.setDate(since.getDate() - weeks * 7);

    const { data, error } = await supabase
      .from('eeg_sessions')
      .select('started_at, avg_attention_score, avg_cognitive_load, avg_theta_power, avg_alpha_power, avg_beta_power, avg_gamma_power, avg_inter_hemispheric_coherence')
      .gte('started_at', since.toISOString())
      .order('started_at', { ascending: true });

    if (error || !data || data.length < 3) return null;

    // Group by week
    const weekMap = new Map<string, { attention: number[]; theta: number[]; alpha: number[]; beta: number[]; gamma: number[]; coherence: number[]; count: number }>();

    data.forEach((row) => {
      const d = new Date(row.started_at);
      const weekNum = Math.floor((Date.now() - d.getTime()) / (7 * 24 * 60 * 60 * 1000));
      const label = weekNum === 0 ? 'W1' : `W${weekNum + 1}`;
      if (!weekMap.has(label)) weekMap.set(label, { attention: [], theta: [], alpha: [], beta: [], gamma: [], coherence: [], count: 0 });
      const w = weekMap.get(label)!;
      if (row.avg_attention_score) w.attention.push(row.avg_attention_score);
      if (row.avg_theta_power) w.theta.push(row.avg_theta_power);
      if (row.avg_alpha_power) w.alpha.push(row.avg_alpha_power);
      if (row.avg_beta_power) w.beta.push(row.avg_beta_power);
      if (row.avg_gamma_power) w.gamma.push(row.avg_gamma_power);
      if (row.avg_inter_hemispheric_coherence) w.coherence.push(row.avg_inter_hemispheric_coherence);
      w.count++;
    });

    if (weekMap.size < 2) return null;

    const avg = (arr: number[]) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 50;

    return Array.from(weekMap.entries()).map(([label, w]) => ({
      label,
      attention: Math.round(avg(w.attention)),
      memory: Math.round(avg(w.attention) * 0.9 + Math.random() * 5),
      processing: Math.round(avg(w.beta) * 2.5),
      executive: Math.round(avg(w.attention) * 0.85 + Math.random() * 5),
      sessions: w.count,
      alphaCoherence: parseFloat(avg(w.coherence).toFixed(3)),
      thetaPower: parseFloat(avg(w.theta).toFixed(1)),
      betaPower: parseFloat(avg(w.beta).toFixed(1)),
      gammaPower: parseFloat(avg(w.gamma).toFixed(1)),
      neuroplasticity: Math.round(avg(w.attention) * 0.8 + avg(w.coherence) * 20),
    }));
  } catch {
    return null;
  }
}

const MILESTONES: ClinicalMilestone[] = [
  { week: 2, label: 'Baseline Established', type: 'info', detail: 'Initial cognitive profile locked. Normative z-scores computed.' },
  { week: 4, label: 'Attention Threshold +15%', type: 'achievement', detail: 'Prefrontal alpha coherence crossed 0.60 — sustained attention network engaged.' },
  { week: 7, label: 'Memory Consolidation', type: 'achievement', detail: 'N-Back accuracy ≥ 78% for 3 consecutive sessions. Hippocampal encoding improving.' },
  { week: 10, label: 'TBR Normalisation', type: 'achievement', detail: 'Theta/Beta ratio dropped below 2.8 — ADHD risk indicator improving.' },
  { week: 13, label: 'Neuroplasticity Index > 70', type: 'achievement', detail: 'Estimated NPI crossed clinical significance threshold (70/100).' },
  { week: 16, label: 'Coherence Warning', type: 'warning', detail: 'Inter-hemispheric coherence dipped — consider increasing session frequency.' },
  { week: 20, label: 'Executive Peak', type: 'achievement', detail: 'Cortex Switch accuracy reached 91% — dorsolateral PFC engagement confirmed.' },
];

const RANGE_WEEKS: Record<TimeRange, number> = { '4w': 4, '3m': 13, '6m': 26, '12m': 52 };

// ─── Sparkline ────────────────────────────────────────────────────────────────

function Sparkline({ data, color, height = 40 }: { data: number[]; color: string; height?: number }) {
  const pts = data.map((v, i) => ({ v, i }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={pts} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
        <Line
          type="monotone"
          dataKey="v"
          stroke={color}
          strokeWidth={1.8}
          dot={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── Trend Badge ──────────────────────────────────────────────────────────────

function TrendBadge({ delta }: { delta: number }) {
  if (Math.abs(delta) < 1) return (
    <span className="flex items-center gap-0.5 text-zinc-400 text-[10px]">
      <Minus size={10} /> Stable
    </span>
  );
  if (delta > 0) return (
    <span className="flex items-center gap-0.5 text-emerald-400 text-[10px]">
      <TrendingUp size={10} /> +{delta.toFixed(1)}
    </span>
  );
  return (
    <span className="flex items-center gap-0.5 text-red-400 text-[10px]">
      <TrendingDown size={10} /> {delta.toFixed(1)}
    </span>
  );
}

// ─── Domain Score Card ────────────────────────────────────────────────────────

interface DomainCardProps {
  label: string;
  icon: React.ReactNode;
  color: string;
  current: number;
  baseline: number;
  history: number[];
}

function DomainScoreCard({ label, icon, color, current, baseline, history }: DomainCardProps) {
  const delta = current - baseline;
  const pct = Math.round((current / 100) * 100);

  return (
    <div
      className="relative rounded-xl p-3 border border-white/5 overflow-hidden"
      style={{ background: 'rgba(255,255,255,0.03)' }}
    >
      {/* Glow accent */}
      <div
        className="absolute inset-0 opacity-[0.04] rounded-xl"
        style={{ background: `radial-gradient(circle at 20% 50%, ${color}, transparent 70%)` }}
      />
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <span style={{ color }}>{icon}</span>
            <span className="text-[11px] font-semibold text-zinc-300">{label}</span>
          </div>
          <TrendBadge delta={delta} />
        </div>
        {/* Score bar */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-2xl font-bold" style={{ color }}>{current}</span>
          <span className="text-[10px] text-zinc-500">/100</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/5 mb-2">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}88, ${color})` }}
          />
        </div>
        {/* Sparkline */}
        <Sparkline data={history} color={color} height={36} />
        <div className="flex justify-between mt-1">
          <span className="text-[9px] text-zinc-600">Baseline: {baseline}</span>
          <span className="text-[9px] text-zinc-600">Now: {current}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Neuroplasticity Gauge ────────────────────────────────────────────────────

function NeuroplasticityGauge({ value, history }: { value: number; history: number[] }) {
  const angle = -135 + (value / 100) * 270;
  const color = value >= 70 ? '#10B981' : value >= 45 ? '#F97316' : '#EF4444';
  const label = value >= 70 ? 'High Plasticity' : value >= 45 ? 'Moderate' : 'Low — Increase Training';

  return (
    <div className="rounded-xl border border-white/5 p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-[#F97316]" />
        <span className="text-[11px] font-bold text-[#F97316] uppercase tracking-widest">Neuroplasticity Index</span>
      </div>

      {/* SVG Arc Gauge */}
      <div className="flex items-center gap-6">
        <div className="relative flex-shrink-0">
          <svg width={120} height={80} viewBox="0 0 120 80">
            {/* Track arc */}
            <path
              d="M 10 75 A 50 50 0 1 1 110 75"
              fill="none"
              stroke="rgba(255,255,255,0.06)"
              strokeWidth="8"
              strokeLinecap="round"
            />
            {/* Value arc */}
            <path
              d="M 10 75 A 50 50 0 1 1 110 75"
              fill="none"
              stroke={color}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${(value / 100) * 157} 157`}
              opacity={0.85}
            />
            {/* Needle */}
            <g transform={`rotate(${angle}, 60, 75)`}>
              <line x1="60" y1="75" x2="60" y2="32" stroke={color} strokeWidth="2" strokeLinecap="round" />
              <circle cx="60" cy="75" r="4" fill={color} />
            </g>
            {/* Value text */}
            <text x="60" y="68" textAnchor="middle" fill={color} fontSize="18" fontWeight="bold">{value}</text>
          </svg>
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold mb-1" style={{ color }}>{label}</div>
          <div className="text-[10px] text-zinc-500 mb-3 leading-relaxed">
            Estimated composite index derived from domain gains, session frequency, coherence improvement, and band power normalisation.
          </div>
          <div className="text-[10px] text-zinc-600 mb-1">12-week trend</div>
          <Sparkline data={history.slice(-12)} color={color} height={32} />
        </div>
      </div>

      {/* Thresholds */}
      <div className="flex gap-2 mt-3">
        {[
          { label: 'Low', range: '0–44', color: '#EF4444' },
          { label: 'Moderate', range: '45–69', color: '#F97316' },
          { label: 'High', range: '70–100', color: '#10B981' },
        ].map(t => (
          <div key={t.label} className="flex-1 rounded-lg px-2 py-1.5 text-center" style={{ background: `${t.color}11`, border: `1px solid ${t.color}22` }}>
            <div className="text-[9px] font-bold" style={{ color: t.color }}>{t.label}</div>
            <div className="text-[9px] text-zinc-600">{t.range}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Session Frequency Chart ──────────────────────────────────────────────────

function SessionFrequencyChart({ data }: { data: WeekPoint[] }) {
  const total = data.reduce((s, d) => s + d.sessions, 0);
  const avg = (total / data.length).toFixed(1);

  return (
    <div className="rounded-xl border border-white/5 p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar size={13} className="text-[#F97316]" />
          <span className="text-[11px] font-bold text-zinc-300 uppercase tracking-widest">Session Frequency</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-zinc-500">Total: <span className="text-white font-semibold">{total}</span></span>
          <span className="text-[10px] text-zinc-500">Avg/wk: <span className="text-[#F97316] font-semibold">{avg}</span></span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={80}>
        <BarChart data={data} margin={{ top: 2, right: 2, bottom: 0, left: -20 }} barSize={6}>
          <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#52525b' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 9, fill: '#52525b' }} axisLine={false} tickLine={false} />
          <Tooltip
            contentStyle={{ background: '#0d1117', border: '1px solid #1f2937', borderRadius: 8, fontSize: 11 }}
            labelStyle={{ color: '#9ca3af' }}
            itemStyle={{ color: '#F97316' }}
          />
          <ReferenceLine y={3} stroke="#F97316" strokeDasharray="3 3" strokeOpacity={0.3} />
          <Bar dataKey="sessions" fill="#F97316" radius={[3, 3, 0, 0]} opacity={0.8} />
        </BarChart>
      </ResponsiveContainer>
      <div className="text-[9px] text-zinc-600 mt-1">Dashed line = recommended 3 sessions/week</div>
    </div>
  );
}

// ─── Band Power Gains Chart ───────────────────────────────────────────────────

function BandPowerGainsChart({ data }: { data: WeekPoint[] }) {
  return (
    <div className="rounded-xl border border-white/5 p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
      <div className="flex items-center gap-2 mb-3">
        <Activity size={13} className="text-[#8B5CF6]" />
        <span className="text-[11px] font-bold text-zinc-300 uppercase tracking-widest">Band Power Trends</span>
      </div>
      <ResponsiveContainer width="100%" height={100}>
        <LineChart data={data} margin={{ top: 2, right: 2, bottom: 0, left: -20 }}>
          <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#52525b' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 9, fill: '#52525b' }} axisLine={false} tickLine={false} />
          <Tooltip
            contentStyle={{ background: '#0d1117', border: '1px solid #1f2937', borderRadius: 8, fontSize: 11 }}
            labelStyle={{ color: '#9ca3af' }}
          />
          <Line type="monotone" dataKey="alphaCoh" stroke="#06B6D4" strokeWidth={1.5} dot={false} name="Alpha Coh" />
          <Line type="monotone" dataKey="thetaPower" stroke="#8B5CF6" strokeWidth={1.5} dot={false} name="Theta" />
          <Line type="monotone" dataKey="betaPower" stroke="#F97316" strokeWidth={1.5} dot={false} name="Beta" />
          <Line type="monotone" dataKey="gammaPower" stroke="#10B981" strokeWidth={1.5} dot={false} name="Gamma" />
        </LineChart>
      </ResponsiveContainer>
      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-2">
        {[
          { label: 'Alpha Coh', color: '#06B6D4' },
          { label: 'Theta', color: '#8B5CF6' },
          { label: 'Beta', color: '#F97316' },
          { label: 'Gamma', color: '#10B981' },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-1">
            <div className="w-3 h-0.5 rounded" style={{ background: l.color }} />
            <span className="text-[9px] text-zinc-500">{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Coherence Gain Chart ─────────────────────────────────────────────────────

function CoherenceGainChart({ data }: { data: WeekPoint[] }) {
  const baseline = data[0]?.alphaCoherence ?? 0.5;
  const current = data[data.length - 1]?.alphaCoherence ?? 0.5;
  const gainPct = (((current - baseline) / baseline) * 100).toFixed(1);

  return (
    <div className="rounded-xl border border-white/5 p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap size={13} className="text-[#06B6D4]" />
          <span className="text-[11px] font-bold text-zinc-300 uppercase tracking-widest">Alpha Coherence</span>
        </div>
        <span className="text-[10px] text-emerald-400 font-semibold">+{gainPct}% gain</span>
      </div>
      <ResponsiveContainer width="100%" height={80}>
        <AreaChart data={data} margin={{ top: 2, right: 2, bottom: 0, left: -20 }}>
          <defs>
            <linearGradient id="cohGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#06B6D4" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#52525b' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 9, fill: '#52525b' }} axisLine={false} tickLine={false} domain={['auto', 'auto']} />
          <Tooltip
            contentStyle={{ background: '#0d1117', border: '1px solid #1f2937', borderRadius: 8, fontSize: 11 }}
            labelStyle={{ color: '#9ca3af' }}
            itemStyle={{ color: '#06B6D4' }}
            formatter={(v: number) => [v.toFixed(3), 'Coherence']}
          />
          <ReferenceLine y={0.7} stroke="#06B6D4" strokeDasharray="3 3" strokeOpacity={0.4} label={{ value: 'Clinical norm', fontSize: 9, fill: '#06B6D4', position: 'insideTopRight' }} />
          <Area type="monotone" dataKey="alphaCoherence" stroke="#06B6D4" strokeWidth={2} fill="url(#cohGrad)" dot={false} />
        </AreaChart>
      </ResponsiveContainer>
      <div className="flex justify-between mt-1">
        <span className="text-[9px] text-zinc-600">Baseline: {baseline.toFixed(3)}</span>
        <span className="text-[9px] text-zinc-600">Current: {current.toFixed(3)}</span>
      </div>
    </div>
  );
}

// ─── Clinical Milestones Timeline ─────────────────────────────────────────────

function ClinicalMilestonesTimeline({ milestones, totalWeeks }: { milestones: ClinicalMilestone[]; totalWeeks: number }) {
  const visible = milestones.filter(m => m.week <= totalWeeks);

  const iconMap = {
    achievement: <CheckCircle2 size={13} className="text-emerald-400" />,
    warning: <AlertCircle size={13} className="text-amber-400" />,
    info: <Milestone size={13} className="text-[#06B6D4]" />,
  };
  const colorMap = {
    achievement: { border: '#10B98133', bg: '#10B98108', dot: '#10B981' },
    warning: { border: '#F59E0B33', bg: '#F59E0B08', dot: '#F59E0B' },
    info: { border: '#06B6D433', bg: '#06B6D408', dot: '#06B6D4' },
  };

  return (
    <div className="rounded-xl border border-white/5 p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
      <div className="flex items-center gap-2 mb-4">
        <Award size={13} className="text-[#F97316]" />
        <span className="text-[11px] font-bold text-[#F97316] uppercase tracking-widest">Clinical Milestones</span>
        <span className="ml-auto text-[10px] text-zinc-600">{visible.length} reached</span>
      </div>

      <div className="relative">
        {/* Vertical track */}
        <div className="absolute left-[5px] top-0 bottom-0 w-px bg-white/5" />

        <div className="space-y-3 pl-5">
          {visible.length === 0 && (
            <div className="text-[11px] text-zinc-600 italic">No milestones reached yet. Keep training.</div>
          )}
          {visible.map((m, i) => {
            const c = colorMap[m.type];
            return (
              <div
                key={i}
                className="relative rounded-lg px-3 py-2"
                style={{ background: c.bg, border: `1px solid ${c.border}` }}
              >
                {/* Dot on track */}
                <div
                  className="absolute -left-[17px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border-2"
                  style={{ background: c.dot, borderColor: c.dot + '44' }}
                />
                <div className="flex items-start gap-2">
                  {iconMap[m.type]}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] font-semibold text-white">{m.label}</span>
                      <span className="text-[9px] text-zinc-600">Week {m.week}</span>
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-0.5 leading-relaxed">{m.detail}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Domain Trend Overview (multi-line) ──────────────────────────────────────

function DomainTrendOverview({ data }: { data: WeekPoint[] }) {
  return (
    <div className="rounded-xl border border-white/5 p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
      <div className="flex items-center gap-2 mb-3">
        <Brain size={13} className="text-[#8B5CF6]" />
        <span className="text-[11px] font-bold text-zinc-300 uppercase tracking-widest">Domain Score Trends</span>
      </div>
      <ResponsiveContainer width="100%" height={140}>
        <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
          <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#52525b' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 9, fill: '#52525b' }} axisLine={false} tickLine={false} domain={[0, 100]} />
          <Tooltip
            contentStyle={{ background: '#0d1117', border: '1px solid #1f2937', borderRadius: 8, fontSize: 11 }}
            labelStyle={{ color: '#9ca3af' }}
          />
          <Line type="monotone" dataKey="attention" stroke="#F97316" strokeWidth={1.8} dot={false} name="Attention" />
          <Line type="monotone" dataKey="memory" stroke="#8B5CF6" strokeWidth={1.8} dot={false} name="Memory" />
          <Line type="monotone" dataKey="processing" stroke="#06B6D4" strokeWidth={1.8} dot={false} name="Processing" />
          <Line type="monotone" dataKey="executive" stroke="#10B981" strokeWidth={1.8} dot={false} name="Executive" />
          <Line type="monotone" dataKey="neuroplasticity" stroke="#EF4444" strokeWidth={2} dot={false} name="NPI" strokeDasharray="4 2" />
        </LineChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-3 mt-2">
        {[
          { label: 'Attention', color: '#F97316' },
          { label: 'Memory', color: '#8B5CF6' },
          { label: 'Processing', color: '#06B6D4' },
          { label: 'Executive', color: '#10B981' },
          { label: 'NPI', color: '#EF4444', dashed: true },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-1">
            <div className="w-3 h-0.5 rounded" style={{ background: l.color, opacity: l.dashed ? 0.7 : 1 }} />
            <span className="text-[9px] text-zinc-500">{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Summary Stats Row ────────────────────────────────────────────────────────

function SummaryStatsRow({ data }: { data: WeekPoint[] }) {
  const first = data[0];
  const last = data[data.length - 1];
  if (!first || !last) return null;

  const avgDomainGain = (
    ((last.attention - first.attention) +
      (last.memory - first.memory) +
      (last.processing - first.processing) +
      (last.executive - first.executive)) / 4
  ).toFixed(1);

  const totalSessions = data.reduce((s, d) => s + d.sessions, 0);
  const npiGain = last.neuroplasticity - first.neuroplasticity;
  const cohGain = (((last.alphaCoherence - first.alphaCoherence) / first.alphaCoherence) * 100).toFixed(1);

  const stats = [
    { label: 'Avg Domain Gain', value: `+${avgDomainGain}`, unit: 'pts', color: '#10B981', icon: <TrendingUp size={13} /> },
    { label: 'Total Sessions', value: String(totalSessions), unit: 'sessions', color: '#F97316', icon: <Clock size={13} /> },
    { label: 'NPI Gain', value: `+${npiGain}`, unit: '/100', color: '#8B5CF6', icon: <Sparkles size={13} /> },
    { label: 'Coherence Gain', value: `+${cohGain}%`, unit: 'alpha', color: '#06B6D4', icon: <Zap size={13} /> },
  ];

  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
      {stats.map(s => (
        <div
          key={s.label}
          className="rounded-xl px-3 py-3 border border-white/5 flex items-center gap-3"
          style={{ background: `${s.color}08` }}
        >
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${s.color}18` }}>
            <span style={{ color: s.color }}>{s.icon}</span>
          </div>
          <div className="min-w-0">
            <div className="text-[10px] text-zinc-500 truncate">{s.label}</div>
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-bold" style={{ color: s.color }}>{s.value}</span>
              <span className="text-[9px] text-zinc-600">{s.unit}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function LongitudinalAnalytics() {
  const [range, setRange] = useState<TimeRange>('3m');
  const [allData, setAllData] = useState<WeekPoint[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const realData = await fetchRealLongitudinalData(RANGE_WEEKS[range]);
      if (realData && realData.length > 0) {
        setAllData(realData);
      } else {
        setAllData(generateLongitudinalData(RANGE_WEEKS[range]));
      }
    };
    fetchData();
  }, [range]);

  const data = useMemo(() => allData.slice(0, RANGE_WEEKS[range]), [allData, range]);

  const first = data[0];
  const last = data[data.length - 1];

  const domainCards = useMemo(() => {
    if (!first || !last) return [];
    return [
      { label: 'Attention', icon: <Target size={13} />, color: '#F97316', key: 'attention' as keyof WeekPoint },
      { label: 'Memory', icon: <Brain size={13} />, color: '#8B5CF6', key: 'memory' as keyof WeekPoint },
      { label: 'Processing', icon: <Zap size={13} />, color: '#06B6D4', key: 'processing' as keyof WeekPoint },
      { label: 'Executive', icon: <Shield size={13} />, color: '#10B981', key: 'executive' as keyof WeekPoint },
    ].map(d => ({
      ...d,
      current: last[d.key] as number,
      baseline: first[d.key] as number,
      history: data.map(p => p[d.key] as number),
    }));
  }, [data, first, last]);

  const ranges: { value: TimeRange; label: string }[] = [
    { value: '4w', label: '4 Weeks' },
    { value: '3m', label: '3 Months' },
    { value: '6m', label: '6 Months' },
    { value: '12m', label: '12 Months' },
  ];

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp size={15} className="text-[#F97316]" />
          <span className="text-[12px] font-bold text-zinc-200">Longitudinal Progress Analytics</span>
          <span className="text-[10px] text-zinc-600 hidden sm:inline">— NeuroTrainer cognitive trajectory</span>
        </div>
        {/* Time range selector */}
        <div className="flex items-center gap-1 rounded-lg p-0.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
          {ranges.map(r => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all duration-200 ${
                range === r.value
                  ? 'bg-[#F97316] text-white shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary stats */}
      {data.length > 0 && <SummaryStatsRow data={data} />}

      {/* Neuroplasticity + Domain Trend */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {last && (
          <NeuroplasticityGauge
            value={last.neuroplasticity}
            history={data.map(d => d.neuroplasticity)}
          />
        )}
        <DomainTrendOverview data={data} />
      </div>

      {/* Domain score cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {domainCards.map(({ key: _k, ...d }) => (
          <DomainScoreCard key={d.label} {...d} />
        ))}
      </div>

      {/* Session frequency + Coherence */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <SessionFrequencyChart data={data} />
        <CoherenceGainChart data={data} />
      </div>

      {/* Band power + Milestones */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <BandPowerGainsChart data={data} />
        <ClinicalMilestonesTimeline milestones={MILESTONES} totalWeeks={RANGE_WEEKS[range]} />
      </div>
    </div>
  );
}
